import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Camera, RefreshCw, Check, Loader2, CameraOff } from "lucide-react";

// Live camera capture (camera-only). Flow: Ambil Foto -> Preview -> Gunakan/Ulangi.
// No file/gallery input by design (mandatory for part condition photos).
export default function CameraCapture({ open, onClose, onCapture, title = "Ambil Foto" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null);
  const [err, setErr] = useState(null);
  const [starting, setStarting] = useState(false);

  const stop = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setShot(null); setErr(null); setStarting(true);
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play().catch(() => {}); }
      } catch {
        setErr("Kamera tidak tersedia atau izin ditolak. Aktifkan izin kamera untuk mengambil foto.");
      } finally { setStarting(false); }
    })();
    return () => { cancelled = true; stop(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const snap = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth; c.height = v.videoHeight;
    c.getContext("2d").drawImage(v, 0, 0);
    setShot(c.toDataURL("image/jpeg", 0.9));
  };

  const confirm = async () => {
    const blob = await (await fetch(shot)).blob();
    const file = new File([blob], `camera-${Date.now()}.jpg`, { type: "image/jpeg" });
    stop();
    onCapture(file);
  };

  const close = () => { stop(); onClose(); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2"><Camera size={18} className="text-[var(--gold)]" />{title}</DialogTitle>
        </DialogHeader>
        {err ? (
          <div className="text-center py-10 text-red-600" data-testid="camera-error"><CameraOff className="mx-auto mb-2" /> {err}</div>
        ) : (
          <div className="space-y-3">
            <div className="relative rounded-lg overflow-hidden bg-black aspect-[4/3] flex items-center justify-center">
              {shot ? <img src={shot} alt="preview" className="w-full h-full object-contain" />
                : <video ref={videoRef} playsInline muted className="w-full h-full object-cover" />}
              {starting && <Loader2 className="animate-spin text-white absolute" />}
            </div>
            {!shot ? (
              <Button onClick={snap} className="w-full bg-gold text-white h-11" data-testid="camera-snap"><Camera size={18} className="mr-2" />Ambil Foto</Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 h-11" onClick={() => setShot(null)} data-testid="camera-retake"><RefreshCw size={16} className="mr-2" />Ulangi</Button>
                <Button className="flex-1 h-11 bg-emerald-600 text-white" onClick={confirm} data-testid="camera-confirm"><Check size={16} className="mr-2" />Gunakan</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
